import { Context } from '../../context/Context';
import { Camera } from '../../camera';
import { Scene } from '../../scene/Scene';
import { GBuffer } from '../GBuffer';
import { DecalRenderer } from '../DecalRenderer';
import { InstanceGroup } from '../InstanceGroup';
import { DrawableObject, RenderMode, RenderSettings } from '../index';
import { Material } from '../../material';
import { GBufferShader, GBufferShaderSettings } from '../../shader/generated';

const WHITE_VEC4 = new Float32Array([1, 1, 1, 1]);

/**
 * Geometry pass: writes the G-buffer (albedo / normal / position / material).
 * Submits instanced groups (with LOD + dithered crossfade) and non-instanced
 * objects, then runs decals on top of the resulting albedo.
 */
export class GBufferPass {
  readonly gBuffer = new GBuffer();
  private readonly decalRenderer = new DecalRenderer();

  init(ctx: Context): void {
    this.gBuffer.init(ctx);
    this.decalRenderer.init(ctx);
  }

  destroy(ctx: Context): void {
    this.gBuffer.destroy(ctx.gl);
    this.decalRenderer.destroy(ctx.gl);
  }

  render(
    ctx: Context,
    scene: Scene,
    camera: Camera,
    instanceGroups: InstanceGroup[],
    nonInstancedObjects: DrawableObject[],
    crossfadeEnabled: boolean,
  ): void {
    const gl = ctx.gl;

    // bind() also clears all texture units to prevent feedback loops
    this.gBuffer.bind(ctx);
    gl.enable(gl.DEPTH_TEST);
    gl.depthMask(true);

    // Draw instanced groups first
    this.renderInstancedGBuffer(ctx, gl, camera, instanceGroups, crossfadeEnabled);

    // Draw non-instanced objects
    const gbufferSettings: RenderSettings = {
      mode: RenderMode.GBuffer,
      lights: [],
      objects: nonInstancedObjects,
    };
    for (const obj of nonInstancedObjects) {
      obj.render(ctx, camera, gbufferSettings);
    }

    this.gBuffer.unbind(gl);

    // Decal pass — modifies G-buffer albedo before lighting
    if (scene.decals.length > 0) {
      this.decalRenderer.render(ctx, this.gBuffer, scene.decals, camera);
    }
  }

  private renderInstancedGBuffer(
    ctx: Context,
    gl: WebGL2RenderingContext,
    camera: Camera,
    groups: InstanceGroup[],
    crossfade: boolean,
  ): void {
    for (const group of groups) {
      // Draw LOD 0 instances (exclude transition zone when crossfade is active)
      this.bindGBufferGroupShader(ctx, camera, group.getGBufferSettings(), group.material);
      group.draw(gl, crossfade);

      // Draw LOD transitions + additional LOD levels
      for (let i = 0; i < group.lodVisibleCounts.length - 1; i++) {
        const trans = group.lodTransitions[i];

        if (crossfade && trans && trans.count > 0) {
          // Fading OUT: draw transition instances with current LOD mesh + dither
          // Positive fade = discard low bayer values (progressively transparent)
          const fadeOutSettings = [
            ...(i === 0 ? group.getGBufferSettings() : group.getLodGBufferSettings(i - 1)),
            'CROSSFADE',
          ] as GBufferShaderSettings;
          const fadeOutMat = i === 0 ? group.material : group.getLodMaterial(i - 1);
          const s0 = this.bindGBufferGroupShader(ctx, camera, fadeOutSettings, fadeOutMat);
          s0.uniformsContainer.setDitherFade(trans.avgFade);
          s0.validate();
          group.drawTransition(gl, i, 0);

          // Fading IN: draw transition instances with next LOD mesh + complementary dither
          // Negative fade = discard high bayer values (fills the holes left by fade-out)
          const fadeInSettings = [
            ...group.getLodGBufferSettings(i),
            'CROSSFADE',
          ] as GBufferShaderSettings;
          const s1 = this.bindGBufferGroupShader(
            ctx,
            camera,
            fadeInSettings,
            group.getLodMaterial(i),
          );
          s1.uniformsContainer.setDitherFade(-trans.avgFade);
          s1.validate();
          group.drawTransition(gl, i, 1);
        }

        // Draw LOD i+1 instances
        if ((group.lodVisibleCounts[i + 1] ?? 0) === 0) continue;
        this.bindGBufferGroupShader(
          ctx,
          camera,
          group.getLodGBufferSettings(i),
          group.getLodMaterial(i),
        );
        group.drawLod(gl, i, crossfade);
      }
    }
  }

  private bindGBufferGroupShader(
    ctx: Context,
    camera: Camera,
    settings: GBufferShaderSettings,
    material: Material | undefined,
  ): GBufferShader {
    const shader = ctx.engine.shaderManager.getGBufferShader(...settings);
    ctx.setShader(shader);

    shader.uniformsContainer
      .setViewMatrix(camera.modelViewMatrix)
      .setProjectionMatrix(camera.projectionMatrix)
      .setUnjitteredViewProjection(camera.unjitteredViewProjection)
      .setPreviousViewProjection(camera.previousViewProjection);

    if (material?.baseColor) {
      shader.uniformsContainer.setTextureSampler(material.baseColor);
      if (material.normalMap) {
        shader.uniformsContainer.setNormalMapSampler(material.normalMap);
      }
      if (material.metallicRoughnessMap) {
        shader.uniformsContainer.setMetallicRoughnessSampler(material.metallicRoughnessMap);
      }
    } else {
      shader.uniformsContainer.setPresetColor(
        (material?.color as number[]) ?? WHITE_VEC4,
      );
    }

    shader.validate();
    return shader;
  }
}
